// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter, Slot } from '@medplum/fhirtypes';
import { restoreOverlappingSlots } from './booking';

export interface CancelAppointmentResult {
  appointmentId: string;
  slotReleased: boolean;
  slotsRestored: number;
  encounterCancelled: boolean;
}

/**
 * Cancels an appointment and releases the associated slot.
 * Updates Appointment status to 'cancelled', Slot status to 'free',
 * Encounter status to 'cancelled', and restores overlapping typed slots
 * that were blocked when this appointment was booked.
 *
 * Concurrent-cancellation safe: uses optimistic locking (meta.versionId)
 * on slot updates and skips already-cancelled appointments.
 *
 * @param medplum - The Medplum client
 * @param appointmentId - The FHIR Appointment resource ID to cancel
 * @param reason - Optional cancellation reason
 * @returns Result indicating what was cancelled and restored
 */
export async function cancelAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  reason?: string
): Promise<CancelAppointmentResult> {
  const result: CancelAppointmentResult = {
    appointmentId,
    slotReleased: false,
    slotsRestored: 0,
    encounterCancelled: false,
  };

  // Read and guard against double-cancellation (idempotency)
  const appointment = await medplum.readResource('Appointment', appointmentId);
  if (appointment.status === 'cancelled') {
    return result;
  }
  if (appointment.status === 'fulfilled') {
    throw new Error(`Cannot cancel fulfilled Appointment/${appointmentId}`);
  }

  // Update Appointment status to cancelled (version-aware via spread)
  const updatedAppointment: Appointment = {
    ...appointment,
    status: 'cancelled',
  };

  if (reason) {
    updatedAppointment.cancelationReason = {
      text: reason,
    };
  }

  try {
    await medplum.updateResource(updatedAppointment);
  } catch {
    // Version conflict — another concurrent cancellation already updated it.
    // Re-read to confirm it's now cancelled; if so, return early.
    const current = await medplum.readResource('Appointment', appointmentId);
    if (current.status === 'cancelled') {
      return result;
    }
    throw new Error('Failed to cancel appointment due to a concurrent update. Please try again.');
  }

  // Release the associated Slot (set back to 'free') and restore overlapping slots.
  // The slot update uses meta.versionId from the read — a concurrent cancellation
  // modifying the same slot will cause a version conflict, which we handle gracefully.
  if (appointment.slot && appointment.slot.length > 0) {
    for (const slotRef of appointment.slot) {
      if (slotRef.reference) {
        try {
          const slotId = slotRef.reference.replace('Slot/', '');
          const slot = await medplum.readResource('Slot', slotId);
          if (slot.status === 'busy') {
            const freedSlot = await medplum.updateResource<Slot>({
              ...slot,
              status: 'free',
            });
            result.slotReleased = true;

            // Restore overlapping typed slots that were blocked when this slot was booked
            const restored = await restoreOverlappingSlots(medplum, freedSlot);
            result.slotsRestored += restored;
          }
        } catch {
          // Slot was deleted or already freed by a concurrent cancellation — safe to skip
        }
      }
    }
  }

  // Cancel the associated Encounter
  const encounters = await medplum.searchResources('Encounter', {
    appointment: `Appointment/${appointmentId}`,
    _count: '1',
  });

  if (encounters.length > 0) {
    const encounter = encounters[0];
    if (encounter.id && encounter.status !== 'cancelled' && encounter.status !== 'finished') {
      try {
        await medplum.updateResource<Encounter>({
          ...encounter,
          status: 'cancelled',
        });
        result.encounterCancelled = true;
      } catch {
        // Version conflict — encounter already cancelled by concurrent operation
      }
    }
  }

  return result;
}
